import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleInfo, faXmark } from '@fortawesome/free-solid-svg-icons';

import 'Components/Modals/AccountModal.scss';
import 'Components/Utility/SteamDeck/SteamDeckInformation.scss';

export default function SteamDeckInformation({ type, banners, longTitle, details, product, note }) {
  const [openModal, setOpenModal] = useState(false);
  const [steamDeckType, setSteamDeckType] = useState('');

  useEffect(() => {
    if (type) {
      setSteamDeckType(type);
    }
  }, [type, setSteamDeckType]);

  const bannerClick = (bannerType) => {
    setSteamDeckType(bannerType);
    setOpenModal(true);
  };

  if (details) {
    details.sort(a => ((a.type === 'warning') ? 1 : -1));
  }

  return (
    <React.Fragment>
      {banners &&
        <div className="steam-deck-banners-container">
          <div className="steam-deck-banner-container">
            <button className="steam-deck-banner" onClick={() => { bannerClick('verified'); }}>
              <div className="steam-deck-icon verified">
                <FontAwesomeIcon icon={faCircleCheck} />
              </div>
              <div className="copy-title-container">
                <div className="title-container">
                  <FormattedMessage id="steam-deck-banner.verified.title" defaultMessage="Verified" />
                </div>
                <div className="copy-container">
                  <FormattedMessage
                    id="steam-deck-banner.verified.copy"
                    defaultMessage="Valve has verified that the game works great on Steam Deck, right out of the box. {link}"
                    values={{
                      link: <span className="copy-link"><FormattedMessage id="steam-deck-banner.verified.copy.link" defaultMessage="Find out more." /></span>,
                    }}
                  />
                </div>
              </div>
            </button>
          </div>
          <div className="steam-deck-banner-container">
            <button className="steam-deck-banner" onClick={() => { bannerClick('playable'); }}>
              <div className="steam-deck-icon playable">
                <FontAwesomeIcon icon={faCircleInfo} />
              </div>
              <div className="copy-title-container">
                <div className="title-container">
                  <FormattedMessage id="steam-deck-banner.playable.title" defaultMessage="Playable" />
                </div>
                <div className="copy-container">
                  <FormattedMessage
                    id="steam-deck-banner.playable.copy"
                    defaultMessage="The game may require some manual tweaking by the user to play. {link}"
                    values={{
                      link: <span className="copy-link"><FormattedMessage id="steam-deck-banner.playable.copy.link" defaultMessage="Find out more." /></span>,
                    }}
                  />
                </div>
              </div>
            </button>
          </div>
        </div>
      }

      {!banners &&
        <button className="steam-deck-button" onClick={() => { setOpenModal(true); }}>
          <div className={`steam-deck-icon ${steamDeckType}`}>
            {steamDeckType === 'verified' &&
              <FontAwesomeIcon icon={faCircleCheck} />
            }
            {steamDeckType === 'playable' &&
              <FontAwesomeIcon icon={faCircleInfo} />
            }
          </div>
          {longTitle &&
            <React.Fragment>
              <FormattedMessage id="steam-deck.long-title" defaultMessage="Steam Deck" />&nbsp;
            </React.Fragment>
          }
          {steamDeckType}
        </button>
      }

      <Modal isOpen={openModal} toggle={() => setOpenModal(!openModal)} className="steamdeck-modal steam-deck-modal">
        <ModalBody>
          <div className="steamdeck-modal-form">
            <div className="d-flex align-items-center justify-content-center pb-4">
              <div>
                <h2>
                  <FormattedMessage id="steam-deck-modal.compatibility.title" defaultMessage="Steam Deck Compatibility" />
                </h2>
              </div>
              <div className={`steam-deck-icon ${steamDeckType}`}>
                {steamDeckType === 'verified' &&
                  <FontAwesomeIcon icon={faCircleCheck} />
                }
                {steamDeckType === 'playable' &&
                  <FontAwesomeIcon icon={faCircleInfo} />
                }
              </div>
            </div>
            <div className="steamdeck-modal-desc pt-2 pb-4">
              <h6>
                {steamDeckType === 'playable' &&
                  <FormattedMessage
                    id="steam-deck-modal.compatibility.playable.desc"
                    defaultMessage="Valve’s testing indicates that {productName} is {type} on Steam Deck. This game is functional on Steam Deck, but might require extra effort to interact with or configure."
                    values={{
                      productName: product,
                      type: steamDeckType,
                    }}
                  />
                }
                {steamDeckType === 'verified' &&
                  <FormattedMessage
                    id="steam-deck-modal.compatibility.verified.desc"
                    defaultMessage="Valve’s testing indicates that {productName} is {type} on Steam Deck. This game is fully functional on Steam Deck, and works great with the built-in controls and display."
                    values={{
                      productName: product,
                      type: steamDeckType,
                    }}
                  />
                }
              </h6>
            </div>

            <div className={`steamdeck-modal-reasons ${details.length ? 'pb-4' : ''}`}>
              {details.map(detail =>
                <div className="d-flex align-items-center mb-2" key={detail._id}>
                  <div className={`steam-deck-icon steam-deck-status-${detail.type}`}>
                    {detail.type === 'ok' ? <FontAwesomeIcon icon={faCircleCheck} /> : <FontAwesomeIcon icon={faCircleInfo} />}
                  </div>
                  <div className="playable-point-text">
                    <FormattedMessage
                      id="steam-deck-modal.playable.reason"
                      defaultMessage="{detailInfo}"
                      values={{
                        detailInfo: detail.info,
                      }}
                    />
                  </div>
                </div>,
              )}
            </div>
            {note &&
              <div className="pb-4">
                <FormattedMessage
                  id="steam-deck-modal.notes"
                  defaultMessage="{notes}"
                  values={{
                    notes: note,
                  }}
                />
              </div>
            }
          </div>
          <div className="d-flex align-items-center justify-content-center">
            <button className="close-btn btn btn-primary" onClick={() => { setOpenModal(false); }}>
              Close
            </button>
          </div>
        </ModalBody>

        <div className="close-button-container">
          <button aria-label="close" onClick={() => { setOpenModal(false); }}>
            <FontAwesomeIcon icon={faXmark} title="Close modal" />
          </button>
        </div>
      </Modal>
    </React.Fragment>
  );
}

SteamDeckInformation.propTypes = {
  type: PropTypes.oneOf([
    'verified',
    'playable',
    '',
  ]),
  banners: PropTypes.bool,
  longTitle: PropTypes.bool,
  details: PropTypes.arrayOf(PropTypes.shape({})),
  note: PropTypes.string,
};

SteamDeckInformation.defaultProps = {
  type: '',
  banners: false,
  longTitle: false,
  details: [],
  note: '',
};
